import * as B from '@blueprintjs/core';
import {Elements, PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import moment from 'moment';
import React from 'react';

import {getStripePromise} from 'app/utils/stripeUtils';

import {STRIPE_APPEARANCE, STRIPE_FONTS} from '../appearance';
import {BILLING_NOT_CONFIGURED_MESSAGE, usePaymentMethodSetupIntent} from '../hooks';
import cs from './styles.module.scss';

const Loading: React.FunctionComponent = () => (
  <div className={cs.loading}>
    <B.Spinner size={24} />
  </div>
);

/** A bank account entered by hand is saved, but can't be charged until its microdeposits are verified. */
const MicrodepositsNextSteps: React.FunctionComponent<{
  arrivalDate: number;
  onDone: () => void;
}> = ({arrivalDate, onDone}) => (
  <>
    <B.DialogBody className={cs.body}>
      <B.Callout intent="primary" icon="time" title="Verify this bank account">
        Stripe will send a test deposit to this account by{' '}
        {moment.unix(arrivalDate).utc().format('MMMM D')}. Follow the link in Stripe's email, or
        Verify deposit under Manage payment methods, to confirm it. You can pay with this account
        once it's verified.
      </B.Callout>
    </B.DialogBody>
    <B.DialogFooter actions={<B.Button intent="primary" text="Done" onClick={onDone} />} />
  </>
);

const SetupForm: React.FunctionComponent<{
  onSaved: (paymentMethodId: string | null) => Promise<void> | void;
  onAwaitingMicrodeposits: (arrivalDate: number) => void;
  onCancel: () => void;
}> = ({onSaved, onAwaitingMicrodeposits, onCancel}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSaving, setIsSaving] = React.useState(false);
  const [isElementReady, setIsElementReady] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSave = async () => {
    if (!stripe || !elements) {
      return;
    }
    setIsSaving(true);
    setError(null);

    // `redirect: 'if_required'` keeps 3D Secure in a Stripe-hosted modal over this page instead of
    // navigating away and back through a return_url.
    // Saving to the organization is the consent to reuse it; the element has no save checkbox to
    // record that, so the method would otherwise carry allow_redisplay: unspecified.
    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {payment_method_data: {allow_redisplay: 'always'}},
    });

    if (result.error) {
      setError(result.error.message ?? 'This payment method could not be saved.');
      setIsSaving(false);
      return;
    }

    const {setupIntent} = result;
    const microdeposits = setupIntent.next_action?.verify_with_microdeposits;
    if (microdeposits) {
      onAwaitingMicrodeposits(microdeposits.arrival_date);
      return;
    }

    // Only a finished setup has attached the method to the customer.
    const paymentMethod = setupIntent.status === 'succeeded' ? setupIntent.payment_method : null;
    await onSaved(typeof paymentMethod === 'string' ? paymentMethod : (paymentMethod?.id ?? null));
  };

  return (
    <>
      <B.DialogBody className={cs.body}>
        {!isElementReady && <Loading />}
        {/* Stripe's iframe measures itself, so it stays laid out — not display:none — while it
            mounts, and grows into the height the body has already reserved. */}
        <div className={isElementReady ? undefined : cs.mounting}>
          <PaymentElement options={{layout: 'tabs'}} onReady={() => setIsElementReady(true)} />
        </div>
        {error && <div className={cs.error}>{error}</div>}
      </B.DialogBody>
      <B.DialogFooter
        actions={
          <>
            <B.Button text="Cancel" disabled={isSaving} onClick={onCancel} />
            <B.Button
              intent="primary"
              text="Save"
              loading={isSaving}
              disabled={!stripe || !elements || !isElementReady}
              onClick={handleSave}
            />
          </>
        }
      />
    </>
  );
};

/**
 * Adds a card or bank account to the organization's Stripe customer. Stripe collects and stores it
 * against a SetupIntent; nothing about it passes through Lens beyond the payment method id.
 */
export const AddPaymentMethodDialog: React.FunctionComponent<{
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
  /** The saved payment method's id, or null when Stripe hasn't attached it to the customer. */
  onSaved: (paymentMethodId: string | null) => Promise<void> | void;
}> = ({organizationId, isOpen, onClose, onSaved}) => {
  const setupIntentQuery = usePaymentMethodSetupIntent(organizationId, isOpen);
  const [microdepositsArrivalDate, setMicrodepositsArrivalDate] = React.useState<number | null>(
    null
  );

  // The account is already saved by the time Stripe asks for microdeposits, so any way out of the
  // next steps refreshes the list.
  const finishMicrodeposits = () => void onSaved(null);

  return (
    <B.Dialog
      isOpen={isOpen}
      onClose={microdepositsArrivalDate === null ? onClose : finishMicrodeposits}
      // Cleared only once the close animation ends, so the used SetupIntent's form doesn't remount
      // while the dialog fades out.
      onClosed={() => setMicrodepositsArrivalDate(null)}
      title="Add a payment method"
      // Stripe opens its bank login in a modal outside this dialog, which would otherwise pull
      // focus back from it and close on a click inside it.
      enforceFocus={false}
      canOutsideClickClose={false}
    >
      {microdepositsArrivalDate !== null ? (
        <MicrodepositsNextSteps
          arrivalDate={microdepositsArrivalDate}
          onDone={finishMicrodeposits}
        />
      ) : setupIntentQuery.data ? (
        <Elements
          // A new SetupIntent means a new Elements tree; Stripe can't swap the secret in place.
          key={setupIntentQuery.data.clientSecret}
          stripe={getStripePromise(setupIntentQuery.data.stripeMode)}
          options={{
            clientSecret: setupIntentQuery.data.clientSecret,
            appearance: STRIPE_APPEARANCE,
            fonts: STRIPE_FONTS,
          }}
        >
          <SetupForm
            onSaved={onSaved}
            onAwaitingMicrodeposits={setMicrodepositsArrivalDate}
            onCancel={onClose}
          />
        </Elements>
      ) : (
        // Same body height and footer as the loaded form, so the dialog opens at the size it
        // keeps rather than growing once Stripe answers.
        <>
          <B.DialogBody className={cs.body}>
            {setupIntentQuery.isError ? (
              <div className={cs.error}>
                {setupIntentQuery.error instanceof Error && setupIntentQuery.error.message
                  ? setupIntentQuery.error.message
                  : BILLING_NOT_CONFIGURED_MESSAGE}
              </div>
            ) : (
              <Loading />
            )}
          </B.DialogBody>
          <B.DialogFooter
            actions={
              setupIntentQuery.isError ? (
                <B.Button text="Close" onClick={onClose} />
              ) : (
                <>
                  <B.Button text="Cancel" onClick={onClose} />
                  <B.Button intent="primary" text="Save" disabled />
                </>
              )
            }
          />
        </>
      )}
    </B.Dialog>
  );
};
